"use client";

import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import { Controller, FormProvider, useFormContext, useFormState, type ControllerProps, type FieldPath, type FieldValues } from "react-hook-form";
import { cn } from "@src/lib/utils";
import { Label } from "@src/components/ui/label";
const Form = FormProvider;
type FormFieldContextValue<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> = {
  name: TName;
};
const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);
const FormField = <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return <FormFieldContext.Provider value={{
    name: props.name
  }} data-sentry-element="FormFieldContext.Provider" data-sentry-component="FormField" data-sentry-source-file="form.tsx">
      <Controller {...props} data-sentry-element="Controller" data-sentry-source-file="form.tsx" />
    </FormFieldContext.Provider>;
};
const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const {
    getFieldState
  } = useFormContext();
  const formState = useFormState({
    name: fieldContext.name
  });
  const fieldState = getFieldState(fieldContext.name, formState);
  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }
  const {
    id
  } = itemContext;
  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState
  };
};
type FormItemContextValue = {
  id: string;
};
const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);
function FormItem({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const id = React.useId();
  return <FormItemContext.Provider value={{
    id
  }} data-sentry-element="FormItemContext.Provider" data-sentry-component="FormItem" data-sentry-source-file="form.tsx">
      <div data-slot="form-item" className={cn("grid gap-2", className)} {...props} />
    </FormItemContext.Provider>;
}
function FormLabel({
  className,
  ...props
}: React.ComponentProps<typeof LabelPrimitive.Root>) {
  const {
    error,
    formItemId
  } = useFormField();
  return <Label data-slot="form-label" data-error={!!error} className={cn("data-[error=true]:text-destructive", className)} htmlFor={formItemId} {...props} data-sentry-element="Label" data-sentry-component="FormLabel" data-sentry-source-file="form.tsx" />;
}
function FormControl({
  ...props
}: React.ComponentProps<typeof Slot>) {
  const {
    error,
    formItemId,
    formDescriptionId,
    formMessageId
  } = useFormField();
  return <Slot data-slot="form-control" id={formItemId} aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`} aria-invalid={!!error} {...props} data-sentry-element="Slot" data-sentry-component="FormControl" data-sentry-source-file="form.tsx" />;
}
function FormDescription({
  className,
  ...props
}: React.ComponentProps<"p">) {
  const {
    formDescriptionId
  } = useFormField();
  return <p data-slot="form-description" id={formDescriptionId} className={cn("text-muted-foreground text-sm", className)} {...props} data-sentry-component="FormDescription" data-sentry-source-file="form.tsx" />;
}
function FormMessage({
  className,
  ...props
}: React.ComponentProps<"p">) {
  const {
    error,
    formMessageId
  } = useFormField();
  const body = error ? String(error?.message ?? "") : props.children;
  if (!body) {
    return null;
  }
  return <p data-slot="form-message" id={formMessageId} className={cn("text-destructive text-sm", className)} {...props} data-sentry-component="FormMessage" data-sentry-source-file="form.tsx">
      {body}
    </p>;
}
export { useFormField, Form, FormItem, FormLabel, FormControl, FormDescription, FormMessage, FormField };